// @ts-ignore
import React from "react";

import { css } from "@compiled/react";
import { useIntl, FormattedMessage } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import {
    Card as WplCard,
    CardProps as WplCardProps
} from "@/components/wpl/card/src/";
import { useLocalizedHref } from "@/libraries/web-framework";
import { Row } from "@/libraries/wpl/foundations/src";

const pageSection = css({
    marginInlineEnd: "auto",
    marginInlineStart: "auto",
    minWidth: "100%",
    padding: `${token("space.0")} ${token("space.0")} ${token(
        "space.400"
    )} ${token("space.0")}`,
    clear: "both",
    position: "relative",

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [".row-layout + .row-layout"]: {
        marginBlockStart: 0
    }
});

const divider = css({
    height: "2px",
    borderColor: "#dfe1e5",
    backgroundColor: "#dfe1e5",
    width: "100%",
    border: 0,
    [media.above.md]: {
        margin: "64px auto 32px"
    },
    [media.below.md]: {
        margin: "32px 0 16px"
    }
});

const resourcesHeader = css({
    textAlign: "center",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    h2: {
        color: "#253858",
        fontWeight: 500,
        margin: "16px 0"
    },
    [media.above.sm]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        h2: {
            fontSize: "2.25rem"
        }
    },
    [media.below.sm]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        h2: {
            fontSize: "1.5rem"
        }
    }
});

const cardsContainer = css({
    "display": "grid",
    "maxWidth": "1200px",
    "gap": token("space.400"),
    "marginTop": "48px",
    "marginBottom": "16px",
    [media.above.md]: {
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        gridTemplateAreas: '"report guides" "report itsm"'
    },
    [media.only.sm]: {
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)",
        gridTemplateAreas: '"report guides itsm"'
    },
    [media.below.sm]: {
        gridTemplateRows: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)",
        gridTemplateAreas: '"report" "guides" "itsm"'
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".content-block": {
        [media.below.sm]: {
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            ".img-section": {
                marginBlockEnd: "16px"
            }
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "h3": {
            lineHeight: "32px",
            marginBlockEnd: "16px"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".text-section": {
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            h4: {
                marginBlockEnd: "16px"
            },
            [media.above.sm]: {
                padding: token("space.400")
            },
            [media.below.sm]: {
                "padding": "32px",
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                "h3": {
                    fontSize: "20px"
                },
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                "> *": {
                    margin: 0
                }
            }
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".contentBlock-eyebrow": {
            fontSize: "0.75rem",
            fontWeight: 400,
            lineHeight: "24px",
            letterSpacing: "1px",
            [media.below.sm]: {
                marginBlockEnd: "10px"
            }
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".contentBlock-linkFlex": {
            margin: 0,
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            a: {
                fontWeight: 400,
                margin: 0
            }
        }
    }
});

type CardProps = Pick<
    WplCardProps,
    "pictureProps" | "headerText" | "eyebrowText" | "orientation"
> & {
    primaryLink: {
        text: string;
        dataLabelEnglish: string;
        link: string;
    };
};

const Card = ({
    pictureProps,
    headerText,
    eyebrowText,
    orientation,
    primaryLink
}: CardProps) => (
    <WplCard
        headerText={headerText}
        eyebrowText={eyebrowText}
        pictureProps={pictureProps}
        orientation={orientation}
        ctaProps={{
            primaryLinkProps: {
                type: "primary",
                text: primaryLink.text,
                href: primaryLink.link,
                dataEventProps: {
                    "data-event": "clicked",
                    "data-event-component": "linkButton",
                    "data-event-container": `cardSection`,
                    "data-product-key": "jiraServiceDesk",
                    "data-label": primaryLink.text,
                    "data-label-english": primaryLink.dataLabelEnglish
                }
            }
        }}
    />
);

const CardsSection = () => {
    const intl = useIntl();

    const cardData: Array<CardProps> = [
        {
            pictureProps: {
                image: {
                    src: "https://wac-cdn.atlassian.com/dam/jcr:01c0f236-3cb0-4068-8ca8-30219b394ae3/jsm-tei-report.png?cdnVersion=1290",
                    alt: intl.formatMessage({
                        id: "jira-service-management.resources.card0.image.alt",
                        defaultMessage: `Cover of spiral notebook titled: "The Total Economic Impact TM of Atlassian Jira Service Management"`
                    }),
                    width: 1136,
                    height: 680
                }
            },
            headerText: intl.formatMessage({
                id: "jira-service-management.resources.card0.body",
                defaultMessage:
                    "The Total Economic Impact™ of Atlassian for ITSM"
            }),
            eyebrowText: intl.formatMessage({
                id: "jira-service-management.resources.card0.header",
                defaultMessage: "forrester report"
            }),
            primaryLink: {
                text: intl.formatMessage({
                    id: "jira-service-management.resources.card0.primary.link",
                    defaultMessage: "Get report"
                }),
                dataLabelEnglish: "Get report",
                link: useLocalizedHref(
                    "/jira/service-management/total-economic-impact-hub"
                )
            },
            orientation: "vertical"
        },
        {
            pictureProps: {
                sources: [
                    {
                        srcSet: "https://wac-cdn.atlassian.com/dam/jcr:6d1ac7d5-56e6-4ca0-933a-cf4c28c646e5/jsm-get-started-wide.png?cdnVersion=1290",
                        media: "(max-width: 63.99rem)"
                    }
                ],
                image: {
                    src: "https://wac-cdn.atlassian.com/dam/jcr:cc80a855-d6a0-4083-8f34-b46a3122bcc1/jsm-get-started.png?cdnVersion=1290",
                    alt: intl.formatMessage({
                        id: "jira-service-management.resources.card1.image.alt",
                        defaultMessage:
                            "Customers being helped out at a service desk by an attendant"
                    })
                }
            },
            headerText: intl.formatMessage({
                id: "jira-service-management.resources.card1.body",
                defaultMessage: "Get started with Jira Service Management"
            }),
            eyebrowText: intl.formatMessage({
                id: "jira-service-management.resources.card1.header",
                defaultMessage: "Guides"
            }),
            primaryLink: {
                text: intl.formatMessage({
                    id: "jira-service-management.resources.card1.primary.link",
                    defaultMessage: "Learn more"
                }),
                dataLabelEnglish: "Learn more",
                link: useLocalizedHref(
                    "/software/jira/service-management/product-guide/getting-started/quick-start-guide"
                )
            },
            orientation: "horizontal"
        },
        {
            pictureProps: {
                sources: [
                    {
                        srcSet: "https://wac-cdn.atlassian.com/dam/jcr:e40a81ed-cc02-472d-a62b-f5b2d21d11ef/tips-and-tricks-wide.png?cdnVersion=1290",
                        media: "(max-width: 63.99rem)"
                    }
                ],
                image: {
                    src: "https://wac-cdn.atlassian.com/dam/jcr:e5c74440-938a-4c00-bd28-1bd53a67d798/jsm-tips.png?cdnVersion=1290",
                    alt: intl.formatMessage({
                        id: "jira-service-management.resources.card2.image.alt",
                        defaultMessage:
                            "Two people learning concepts drawn on a black board"
                    })
                }
            },
            headerText: intl.formatMessage({
                id: "jira-service-management.resources.card2.body",
                defaultMessage:
                    "Tips, tricks, and best practices for managing IT service delivery"
            }),
            eyebrowText: intl.formatMessage({
                id: "jira-service-management.resources.card2.header",
                defaultMessage: "ITSM"
            }),
            primaryLink: {
                text: intl.formatMessage({
                    id: "jira-service-management.resources.card2.primary.link",
                    defaultMessage: "Learn more"
                }),
                dataLabelEnglish: "Learn more",
                link: useLocalizedHref("/itsm")
            },
            orientation: "horizontal"
        }
    ];
    return (
        <section>
            <div css={pageSection}>
                <Row>
                    <hr css={divider} />
                </Row>
                <Row>
                    <div css={resourcesHeader}>
                        <FormattedMessage
                            id="jira-service-management.resources.header"
                            defaultMessage="Jira Service Management resources"
                            tagName="h2"
                        />
                    </div>
                </Row>
                <Row>
                    <div css={cardsContainer}>
                        {cardData.map((card, index) => {
                            const cardStyles = [
                                index === 0 &&
                                    css({
                                        "gridArea": "report",
                                        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                        ".content-block": {
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "1rem",
                                            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                            img: {
                                                height: "fit-content",
                                                [media.below.md]: {
                                                    aspectRatio: "auto"
                                                }
                                            }
                                        }
                                    }),
                                index === 1 &&
                                    css({
                                        "gridArea": "guides",
                                        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                        ".content-block": {
                                            [media.above.md]: {
                                                gridTemplateColumns:
                                                    "minmax(0, 1fr) minmax(0, 1.2fr)",
                                                gap: 0
                                            },
                                            [media.below.md]: {
                                                display: "flex",
                                                flexDirection: "column"
                                            }
                                        }
                                    }),
                                index === 2 &&
                                    css({
                                        "gridArea": "itsm",
                                        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                        ".content-block": {
                                            [media.above.md]: {
                                                gridTemplateColumns:
                                                    "minmax(0, 1fr) minmax(0, 1.2fr)",
                                                gap: 0
                                            },
                                            [media.below.md]: {
                                                display: "flex",
                                                flexDirection: "column"
                                            }
                                        }
                                    })
                            ];
                            return (
                                <div key={index} css={cardStyles}>
                                    <Card {...card} />
                                </div>
                            );
                        })}
                    </div>
                </Row>
                <Row>
                    <hr css={divider} />
                </Row>
            </div>
        </section>
    );
};

export default CardsSection;
